/**
 * Slice 49 — the "Your location" explanation shows once per person, before their first start, then never again.
 * Remembered on the device (so it works offline) and on their profile (so a new phone does not ask again).
 */
import { useCallback, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const KEY = (userId: string) => `stravo:locationIntroSeen:${userId}`;

export function useLocationIntro() {
  const { user, profile } = useAuth();
  const [markedNow, setMarkedNow] = useState(false);

  const seenOnDevice = (() => {
    if (!user?.id) return false;
    try {
      return localStorage.getItem(KEY(user.id)) === "1";
    } catch {
      return false;
    }
  })();
  const seenOnProfile = Boolean((profile as { location_intro_seen_at?: string | null } | null)?.location_intro_seen_at);
  const seen = markedNow || seenOnDevice || seenOnProfile;

  const markSeen = useCallback(async () => {
    setMarkedNow(true);
    if (!user?.id) return;
    try {
      localStorage.setItem(KEY(user.id), "1");
    } catch {
      /* ignore */
    }
    try {
      await supabase
        .from("profiles")
        .update({ location_intro_seen_at: new Date().toISOString() } as never)
        .eq("id", user.id);
    } catch {
      /* the device copy still stops it showing again */
    }
  }, [user?.id]);

  return { seen, markSeen };
}
